import React, { useEffect, useState } from 'react';

import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { fetchCollabPermissionToken } from '@confluence/native-collab/entry-points/CollabPermissionsToken.ts';
import { NativeCollabLoader } from '@confluence/native-collab/entry-points/NativeCollabLoader.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { FullPageLoadingScreen } from '@confluence/full-page-loading-screen';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import {
	useEditPageLoadingValues,
	useEditPageLoadingActions,
} from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { FullCycleRenderWrapper } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';

import { EditorContentContainer } from '../EditorContentContainer.tsx';
import { TreeRootForServerPlaceholderWrapper } from '../TreeRootForServerPlaceholderWrapper.tsx';
// eslint-disable-next-line import/no-cycle
import { FullPageEditorLoader } from '../FullPageEditorLoader.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

export type EditPageLoaderProps = {
	contentId: string;
	spaceKey: string;
	draftShareId: string | undefined;
	contentType: string;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
};

const EditPageLoaderInternal = ({
	contentId,
	spaceKey,
	draftShareId,
	contentType,
	ClassicEditorContextProviderLoader,
}: EditPageLoaderProps): React.JSX.Element => {
	const [prevContentId, setPrevContentId] = useState(contentId);
	const [prevContentType, setPrevContentType] = useState(contentType);
	const { referredFromLivePage } = useEditPageLoadingValues();
	const { loadEditor: setEditPageLoadingValues } = useEditPageLoadingActions();
	const [livePageTransitionHandled, setLivePageTransitionHandled] = useState<boolean>(false);

	// This useEffect allows us to unmount and remount the ClassicEditorContextProvider,
	// NativeCollabLoader, and FullPageEditor when switching from one edit page to another edit page.
	// This is necessary to reset the editor properly for edit -> edit transitions.
	useEffect(() => {
		setPrevContentId(contentId);
		setLivePageTransitionHandled(false);
	}, [contentId]);

	useEffect(() => {
		setPrevContentType(contentType);
	}, [contentType]);

	// Be careful when modifying this useEffect as this can break live page -> edit page transitions
	// This effect is a temporary solution to address an issue where the editor does not reset properly when navigating from a live page.
	// The editor tends to hold onto state from the previous page, causing problems when switching between pages if a full unmount doesn't occur.
	// This effects resets the editor by force-unmounting it by showing the loading screen when it detects a transition from a live page.
	useEffect(() => {
		// livePageTransitionHandled is used to prevent the editor from showing the loading screen endlessly
		if (referredFromLivePage && !livePageTransitionHandled) {
			setLivePageTransitionHandled(true);
			// Need to reset this property or it'll be retained when the browser back button is used.
			// In that case, it doesn't go through the flow that sets the value again.
			setEditPageLoadingValues({ referredFromLivePage: undefined });
		}
	}, [referredFromLivePage, livePageTransitionHandled, setEditPageLoadingValues]);

	if (
		contentId !== prevContentId ||
		contentType !== prevContentType ||
		(referredFromLivePage && !livePageTransitionHandled)
	) {
		// isLoading is set to true by a state change, which does not happen immediately after content id changes
		// To make sure loading screen shows up immediately after content id changes, compare new content id with the content id in ref
		// This is smilar to how componentWillUpdate works in a classic react component
		return <FullPageLoadingScreen />;
	}

	const EditPageWrapper =
		ClassicEditorContextProviderLoader ??
		(({ children }) => <div data-testid="edit-page-default-wrapper">{children}</div>);
	return (
		// @ts-ignore - Property 'isWithinEditor' is missing in type '{ children: Element; }' but required in type '{ children: any; isWithinEditor: any; }'
		// This error was introduced after upgrading to TypeScript 5
		<EditPageWrapper>
			<NativeCollabPresenceStateContainer scope={contentId}>
				<NativeCollabLoader
					contentId={contentId}
					spaceKey={spaceKey}
					draftShareId={draftShareId}
					contentType={contentType}
					permissionTokenRefresh={fetchCollabPermissionToken(contentId, draftShareId)}
				>
					{(propsFromNativeCollab: NativeCollabInjectedProps) => {
						if (propsFromNativeCollab.subType === 'live' && !propsFromNativeCollab.isOverviewPage) {
							return <Redirection name={VIEW_PAGE.name} params={{ contentId, spaceKey }} />;
						}

						return isSSRStreaming() ? (
							<SuspenseWithDataFetcher dataFetcherName="content-main">
								<TreeRootForServerPlaceholderWrapper>
									<EditorContentContainer
										contentId={contentId}
										fallback={<FullPageLoadingScreen />}
									>
										<FullPageEditorLoader
											{...propsFromNativeCollab}
											extensionContextProvider={{
												ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
											}}
										/>
									</EditorContentContainer>
								</TreeRootForServerPlaceholderWrapper>
							</SuspenseWithDataFetcher>
						) : (
							// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
							// SSRed loadables. Due to different component trees between server (EditPageLoader) and client (EditPageLoaderServer)
							// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
							// so it matches on both server and client.
							<FullCycleRenderWrapper>
								<TreeRootForServerPlaceholderWrapper>
									<EditorContentContainer
										contentId={contentId}
										fallback={<FullPageLoadingScreen />}
									>
										<FullPageEditorLoader
											{...propsFromNativeCollab}
											extensionContextProvider={{
												ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
											}}
										/>
									</EditorContentContainer>
								</TreeRootForServerPlaceholderWrapper>
							</FullCycleRenderWrapper>
						);
					}}
				</NativeCollabLoader>
			</NativeCollabPresenceStateContainer>
		</EditPageWrapper>
	);
};

export const EditPageLoader: typeof EditPageLoaderInternal = (props) => {
	return (
		<UFOSegment name="edit-page-loader">
			<EditPageLoaderInternal {...props} />
		</UFOSegment>
	);
};
